import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Camera, Upload, User, RefreshCw, X, Bell, Clock, Calendar, Mail, Send, Building2, Users, Copy, Plus, Shield, Trash2, Utensils, Archive, Volume2, Loader2, Eye, FolderOpen, Bus, Download, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// PERMISSION_LABELS is derived from /app/shared/permission_areas.json (symlinked
// into src/shared) so backend and frontend can never drift. Phase 0 change —
// order & keys match the previous inline literal exactly.
import PERMISSION_AREAS_JSON from '../shared/permission_areas.json';
const PERMISSION_LABELS = PERMISSION_AREAS_JSON.labels;

const PRESET_ROLES = [
  {
    name: 'Full Access',
    description: 'Edit access to all features',
    access: { players: 'edit', schedule: 'edit', stats: 'edit', videos: 'edit', communication: 'edit', reports: 'edit', practice_plans: 'edit', transportation: 'edit' }
  },
  {
    name: 'Stats Manager',
    description: 'Stats and reports only',
    access: { players: 'none', schedule: 'none', stats: 'edit', videos: 'none', communication: 'none', reports: 'edit', practice_plans: 'none', transportation: 'none' }
  },
  {
    name: 'Practice Coordinator',
    description: 'Practice plans and schedule',
    access: { players: 'none', schedule: 'edit', stats: 'none', videos: 'none', communication: 'none', reports: 'none', practice_plans: 'edit', transportation: 'none' }
  },
  {
    name: 'Media Manager',
    description: 'Videos and communication',
    access: { players: 'none', schedule: 'none', stats: 'none', videos: 'edit', communication: 'edit', reports: 'none', practice_plans: 'none', transportation: 'none' }
  },
  {
    name: 'Travel Coordinator',
    description: 'Schedule and transportation planning',
    access: { players: 'view', schedule: 'edit', stats: 'none', videos: 'none', communication: 'view', reports: 'none', practice_plans: 'none', transportation: 'edit' }
  },
  {
    name: 'View Only',
    description: 'Can view players and schedule only',
    access: { players: 'view', schedule: 'view', stats: 'none', videos: 'none', communication: 'none', reports: 'none', practice_plans: 'none', transportation: 'none' }
  }
];

export default function SettingsPage({ user, onLogout, teams = [], onSwitchTeam, onUserUpdate, permissions }) {
  const navigate = useNavigate();
  const { accessLevel } = usePermissions(permissions);
  const profileEditable = user.role === 'head_coach' || Object.keys(permissions?.access || {}).some(a => permissions.access[a] === 'edit');
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || null);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Alert schedule state
  const [alertSchedule, setAlertSchedule] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 0,
    hour: 9,
    threshold: 75,
    include_all_players: false
  });
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isTestingAlert, setIsTestingAlert] = useState(false);
  const [nextRun, setNextRun] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  // Team management state
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamCoaches, setTeamCoaches] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [joinTeamCode, setJoinTeamCode] = useState('');
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [limitMessage, setLimitMessage] = useState(null);
  const [seasonName, setSeasonName] = useState('');
  const [archiveCats, setArchiveCats] = useState({ games: true, schedules: true, attendance: true, practice_plans: false, roster: false });
  const [archives, setArchives] = useState([]);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showJoinTeamDialog, setShowJoinTeamDialog] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isJoiningTeam, setIsJoiningTeam] = useState(false);

  // Invite Coach state
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'assistant_coach', preset: 'Full Access' });
  const [isInviting, setIsInviting] = useState(false);
  const [invites, setInvites] = useState([]);

  // Archive Viewer state
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveDetail, setArchiveDetail] = useState(null);
  const [loadingArchive, setLoadingArchive] = useState(false);

  // Team branding state
  const [teamBranding, setTeamBranding] = useState({
    school_name: '',
    mascot_name: 'Leopards',
    mascot_url: null,
    primary_color: '#1d4ed8',
    secondary_color: '#000000',
    accent_color: '#f59e0b'
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [isUploadingMascot, setIsUploadingMascot] = useState(false);
  const mascotInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const transportationFileInputRef = useRef(null);
  const teamMealFileInputRef = useRef(null);
  const [transportationFile, setTransportationFile] = useState(null);
  const [teamMealFile, setTeamMealFile] = useState(null);
  const [isUploadingTransportation, setIsUploadingTransportation] = useState(false);
  const [isUploadingTeamMeal, setIsUploadingTeamMeal] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [isRunningReminders, setIsRunningReminders] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [eventAlerts, setEventAlerts] = useState({
    enabled: true,
    send_hour: 12,
    send_email: true,
    send_push: false
  });
  const [isSavingEventAlerts, setIsSavingEventAlerts] = useState(false);
  const [devDigestEnabled, setDevDigestEnabled] = useState(false);
  const [devDigestFrequency, setDevDigestFrequency] = useState('weekly');
  const [devDigestDay, setDevDigestDay] = useState(0);
  const [isSavingDevDigest, setIsSavingDevDigest] = useState(false);
  const [isTestingDevDigest, setIsTestingDevDigest] = useState(false);
  const [devDigestPreview, setDevDigestPreview] = useState(null);
  const [isLoadingDevDigestPreview, setIsLoadingDevDigestPreview] = useState(false);
  const [devCategories, setDevCategories] = useState([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [isSavingCategories, setIsSavingCategories] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isTestingEventAlerts, setIsTestingEventAlerts] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showAlertHistory, setShowAlertHistory] = useState(false);

  // Quick Links state
  const [quickLinks, setQuickLinks] = useState({
    meal_providers: []
  });
  const [isSavingQuickLinks, setIsSavingQuickLinks] = useState(false);
  const [newMealProvider, setNewMealProvider] = useState({ name: '', url: '' });

  // AI Bot Voice & Tone state
  const [voices, setVoices] = useState([]);
  const [voiceSettings, setVoiceSettings] = useState({ voice_id: '', voice_name: '', tone: '' });
  const [isSavingVoice, setIsSavingVoice] = useState(false);
  const [isPreviewingVoice, setIsPreviewingVoice] = useState(false);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    loadAvatar();
    loadAlertSchedule();
    loadCurrentTeam();
    loadTeamBranding();
    loadQuickLinks();
    loadVoiceSettings();
    loadTransportationFile();
    loadTeamMealFile();
    loadDevDigestSettings();
    if (user.role === 'head_coach') {
      loadTeamCoaches();
      loadDevCategories();
    }
  }, [user.team_id]);

  const loadDevCategories = async () => {
    try {
      const res = await axios.get(`${API}/player-intel/teams/${user.team_id}/categories`, { params: { user_id: user.id } });
      setDevCategories(res.data.categories || []);
    } catch (error) {
      // Not a coach or flag off — leave default
    }
  };

  const saveDevCategories = async (next) => {
    setIsSavingCategories(true);
    try {
      const res = await axios.put(`${API}/player-intel/teams/${user.team_id}/categories`, { user_id: user.id, categories: next });
      setDevCategories(res.data.categories);
      toast.success('Development categories updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    } finally {
      setIsSavingCategories(false);
    }
  };

  const handleAddCategory = () => {
    const val = newCategoryInput.trim();
    if (!val) return;
    if (devCategories.some((c) => c.toLowerCase() === val.toLowerCase())) {
      toast.error('That category already exists');
      return;
    }
    saveDevCategories([...devCategories, val]);
    setNewCategoryInput('');
  };

  const handleRemoveCategory = (cat) => {
    if (devCategories.length <= 1) {
      toast.error('At least one category is required');
      return;
    }
    saveDevCategories(devCategories.filter((c) => c !== cat));
  };

  const loadDevDigestSettings = async () => {
    try {
      const res = await axios.get(`${API}/player-intel/teams/${user.team_id}/digest-settings`, { params: { user_id: user.id } });
      setDevDigestEnabled(res.data.enabled || false);
      setDevDigestFrequency(res.data.frequency || 'weekly');
      setDevDigestDay(res.data.day_of_week ?? 0);
    } catch (error) {
      // Not a coach or flag off — leave default
    }
  };

  const saveDevDigestSettings = async (overrides = {}) => {
    const next = {
      enabled: devDigestEnabled,
      frequency: devDigestFrequency,
      day_of_week: devDigestDay,
      ...overrides,
    };
    setDevDigestEnabled(next.enabled);
    setDevDigestFrequency(next.frequency);
    setDevDigestDay(next.day_of_week);
    setIsSavingDevDigest(true);
    try {
      await axios.put(`${API}/player-intel/teams/${user.team_id}/digest-settings`, { user_id: user.id, ...next });
      toast.success('Development digest settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save digest setting');
    } finally {
      setIsSavingDevDigest(false);
    }
  };

  const testDevDigest = async () => {
    setIsTestingDevDigest(true);
    try {
      const res = await axios.post(`${API}/player-intel/teams/${user.team_id}/digest-settings/test`, { user_id: user.id });
      toast.success(res.data.message || 'Test digest sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setIsTestingDevDigest(false);
    }
  };

  const toggleDevDigestPreview = async () => {
    if (devDigestPreview) {
      setDevDigestPreview(null);
      return;
    }
    setIsLoadingDevDigestPreview(true);
    try {
      const res = await axios.get(`${API}/player-intel/teams/${user.team_id}/digest-preview`, { params: { user_id: user.id } });
      setDevDigestPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load preview');
    } finally {
      setIsLoadingDevDigestPreview(false);
    }
  };

  const loadTransportationFile = async () => {
    try {
      const res = await axios.get(`${API}/transportation/latest?team_id=${user.team_id}`);
      setTransportationFile(res.data || null);
    } catch (e) {
      setTransportationFile(null);
    }
  };

  const loadTeamMealFile = async () => {
    try {
      const res = await axios.get(`${API}/team-meals/latest?team_id=${user.team_id}`);
      setTeamMealFile(res.data || null);
    } catch (e) {
      setTeamMealFile(null);
    }
  };

  const handleTransportationUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIsUploadingTransportation(true);
    try {
      const fd = new FormData();
      fd.append('team_id', user.team_id);
      fd.append('file', f);
      await axios.post(`${API}/transportation/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Transportation file uploaded');
      loadTransportationFile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setIsUploadingTransportation(false);
      if (transportationFileInputRef.current) transportationFileInputRef.current.value = '';
    }
  };

  const viewTransportationFile = async () => {
    try {
      const res = await axios.get(`${API}/templates/transportation?team_id=${user.team_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = transportationFile?.original_filename || 'transportation.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download');
    }
  };

  const handleTeamMealUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setIsUploadingTeamMeal(true);
    try {
      const fd = new FormData();
      fd.append('team_id', user.team_id);
      fd.append('file', f);
      await axios.post(`${API}/team-meals/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Team meal file uploaded');
      loadTeamMealFile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setIsUploadingTeamMeal(false);
      if (teamMealFileInputRef.current) teamMealFileInputRef.current.value = '';
    }
  };

  const viewTeamMealFile = async () => {
    try {
      const res = await axios.get(`${API}/team-meals/template.xlsx?team_id=${user.team_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = teamMealFile?.original_filename || 'team_meals.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to open workbook');
    }
  };

  const loadReminderPreview = async () => {
    try {
      const res = await axios.get(`${API}/transportation/reminders/preview?team_id=${user.team_id}`);
      setReminderPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No transportation file uploaded yet');
    }
  };

  const runReminderNow = async () => {
    setIsRunningReminders(true);
    try {
      const res = await axios.post(`${API}/transportation/reminders/run`, { team_id: user.team_id });
      const sent = res.data?.sent || 0;
      const skipped = res.data?.skipped || 0;
      if (sent > 0) toast.success(`Sent ${sent} bus-company reminder${sent === 1 ? '' : 's'} (${skipped} skipped)`);
      else toast.info(`No reminders sent — ${skipped} skipped (usually: no away games in the next ~48h or phones missing from your workbook)`);
      loadReminderPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run reminders');
    } finally {
      setIsRunningReminders(false);
    }
  };

  const loadVoiceSettings = async () => {
    try {
      const [voicesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/voice/voices`),
        axios.get(`${API}/voice/settings/${user.id}`)
      ]);
      setVoices(voicesRes.data.voices || []);
      setVoiceSettings({
        voice_id: settingsRes.data.voice_id || '',
        voice_name: settingsRes.data.voice_name || '',
        tone: settingsRes.data.tone || ''
      });
    } catch (error) {
      console.error('Failed to load voice settings');
    }
  };

  const handleVoiceChange = (voiceId) => {
    const selected = voices.find((v) => v.voice_id === voiceId);
    setVoiceSettings((prev) => ({ ...prev, voice_id: voiceId, voice_name: selected?.name || '' }));
  };

  const handlePreviewVoice = async () => {
    if (!voiceSettings.voice_id) {
      toast.error('Select a voice first');
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setIsPreviewingVoice(true);
    try {
      const res = await axios.post(`${API}/voice/preview`, { voice_id: voiceSettings.voice_id });
      const audio = new Audio(res.data.audio_b64);
      previewAudioRef.current = audio;
      audio.play();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Voice preview failed');
    }
    setIsPreviewingVoice(false);
  };

  const saveVoiceSettings = async () => {
    setIsSavingVoice(true);
    try {
      await axios.post(`${API}/voice/settings`, {
        user_id: user.id,
        voice_id: voiceSettings.voice_id || '21m00Tcm4TlvDq8ikWAM',
        voice_name: voiceSettings.voice_name || 'Rachel',
        tone: voiceSettings.tone
      });
      toast.success('AI voice & tone saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save voice settings');
    }
    setIsSavingVoice(false);
  };

  const loadAvatar = async () => {
    try {
      const response = await axios.get(`${API}/coach-avatar/${user.id}`);
      if (response.data.avatar_url) {
        setAvatarUrl(`${BACKEND_URL}${response.data.avatar_url}`);
      }
    } catch (error) {
      // No avatar set
    }
  };

  const loadAlertSchedule = async () => {
    try {
      const response = await axios.get(`${API}/settings/alert-schedule/${user.team_id}`);
      setAlertSchedule({
        enabled: response.data.enabled || false,
        frequency: response.data.frequency || 'weekly',
        day_of_week: response.data.day_of_week || 0,
        hour: response.data.hour || 9,
        threshold: response.data.threshold || 75,
        include_all_players: response.data.include_all_players || false
      });
      setNextRun(response.data.next_run);
      setLastRun(response.data.last_run);
    } catch (error) {
      console.error('Failed to load alert schedule');
    }
  };

  const loadCurrentTeam = async () => {
    try {
      const response = await axios.get(`${API}/teams/${user.team_id}`);
      setCurrentTeam(response.data);
    } catch (error) {
      console.error('Failed to load team');
    }
    try {
      const alertsResponse = await axios.get(`${API}/settings/event-alerts/${user.team_id}`);
      setEventAlerts(alertsResponse.data);
    } catch (error) {
      console.error('Failed to load event alert settings');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/teams/${user.team_id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCurrentTeam((prev) => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Team logo uploaded! It now replaces PAC branding in emails and the sidebar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload team logo');
    }
    setIsUploadingLogo(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/teams/${user.team_id}/logo`);
      setCurrentTeam((prev) => ({ ...prev, logo_url: null }));
      toast.success('Team logo removed — reverted to PAC branding');
    } catch (error) {
      toast.error('Failed to remove team logo');
    }
  };

  const saveEventAlerts = async () => {
    setIsSavingEventAlerts(true);
    try {
      await axios.post(`${API}/settings/event-alerts`, {
        team_id: user.team_id,
        ...eventAlerts
      });
      toast.success('Auto event alerts saved');
    } catch (error) {
      toast.error('Failed to save event alert settings');
    }
    setIsSavingEventAlerts(false);
  };

  const loadAlertHistory = async () => {
    try {
      const response = await axios.get(`${API}/settings/event-alerts/${user.team_id}/history`);
      setAlertHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load alert history');
    }
  };

  const loadSubscription = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        axios.get(`${API}/payments/subscription/${user.team_id}`),
        axios.get(`${API}/payments/plans`)
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      console.error('Failed to load subscription info');
    }
  };

  useEffect(() => {
    if (user.role === 'head_coach') loadSubscription();
  }, [user.team_id, user.role]);

  const startCheckout = async (tier) => {
    setCheckoutTier(tier);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        tier,
        origin_url: window.location.origin,
        user_id: user.id,
        team_id: user.team_id
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutTier(null);
    }
  };

  const openBillingPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const response = await axios.post(`${API}/payments/portal`, {
        team_id: user.team_id,
        origin_url: window.location.origin
      });
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setIsOpeningPortal(false);
    }
  };

  const toggleAlertHistory = () => {
    const next = !showAlertHistory;
    setShowAlertHistory(next);
    if (next) loadAlertHistory();
  };

  const testEventAlerts = async () => {
    setIsTestingEventAlerts(true);
    try {
      const response = await axios.post(`${API}/settings/event-alerts/${user.team_id}/test`);
      const d = response.data;
      if (d.events_alerted > 0) {
        toast.success(`Test sent: ${d.events_alerted} event(s), ${d.emails_sent} email(s)`);
      } else {
        toast.info('No games or practices scheduled today — nothing to alert');
      }
    } catch (error) {
      toast.error('Failed to run test alert');
    }
    setIsTestingEventAlerts(false);
  };

  const loadTeamBranding = async () => {
    try {
      const response = await axios.get(`${API}/team-branding/${user.team_id}`);
      setTeamBranding({
        school_name: response.data.school_name || '',
        mascot_name: response.data.mascot_name || 'Leopards',
        mascot_url: response.data.mascot_url ? `${BACKEND_URL}${response.data.mascot_url}` : null,
        primary_color: response.data.primary_color || '#1d4ed8',
        secondary_color: response.data.secondary_color || '#000000',
        accent_color: response.data.accent_color || '#f59e0b'
      });
    } catch (error) {
      console.error('Failed to load team branding');
    }
  };

  const loadQuickLinks = async () => {
    try {
      const response = await axios.get(`${API}/teams/${user.team_id}/quick-links`);
      setQuickLinks({
        meal_providers: response.data.meal_providers || []
      });
    } catch (error) {
      console.error('Failed to load quick links');
    }
  };

  const handleSaveQuickLinks = async () => {
    setIsSavingQuickLinks(true);
    try {
      await axios.put(`${API}/teams/${user.team_id}/quick-links`, {
        meal_providers: quickLinks.meal_providers
      });
      toast.success('Quick links saved successfully!');
    } catch (error) {
      toast.error('Failed to save quick links');
    }
    setIsSavingQuickLinks(false);
  };

  const addMealProvider = () => {
    if (!newMealProvider.name || !newMealProvider.url) {
      toast.error('Please enter both name and URL');
      return;
    }
    setQuickLinks({
      ...quickLinks,
      meal_providers: [...quickLinks.meal_providers, { ...newMealProvider }]
    });
    setNewMealProvider({ name: '', url: '' });
  };

  const removeMealProvider = (index) => {
    setQuickLinks({
      ...quickLinks,
      meal_providers: quickLinks.meal_providers.filter((_, i) => i !== index)
    });
  };

  const handleSaveBranding = async () => {
    setIsSavingBranding(true);
    try {
      await axios.post(`${API}/team-branding?team_id=${user.team_id}`, {
        name: currentTeam?.name || '',
        school_name: teamBranding.school_name,
        mascot_name: teamBranding.mascot_name,
        primary_color: teamBranding.primary_color,
        secondary_color: teamBranding.secondary_color,
        accent_color: teamBranding.accent_color
      });
      toast.success('Team branding saved successfully!');
    } catch (error) {
      toast.error('Failed to save branding');
    }
    setIsSavingBranding(false);
  };

  const handleMascotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploadingMascot(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('team_id', user.team_id);

      const response = await axios.post(`${API}/team-branding/mascot/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTeamBranding(prev => ({
        ...prev,
        mascot_url: `${BACKEND_URL}${response.data.mascot_url}`
      }));
      toast.success('Mascot image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload mascot image');
    }
    setIsUploadingMascot(false);
  };

  const handleDeleteMascot = async () => {
    try {
      await axios.delete(`${API}/team-branding/mascot/${user.team_id}`);
      setTeamBranding(prev => ({ ...prev, mascot_url: null }));
      toast.success('Mascot image removed');
    } catch (error) {
      toast.error('Failed to remove mascot image');
    }
  };

  const loadTeamCoaches = async () => {
    try {
      const response = await axios.get(`${API}/teams/${user.team_id}/coaches`);
      setTeamCoaches(response.data.coaches || []);
    } catch (error) {
      console.error('Failed to load coaches');
    }
  };

  const [showJoinEmail, setShowJoinEmail] = useState(false);
  const [joinEmail, setJoinEmail] = useState({ email: '', name: '', role: 'player' });
  const [joinEmailChoices, setJoinEmailChoices] = useState([]);
  const [isSendingJoinEmail, setIsSendingJoinEmail] = useState(false);
  const openJoinEmail = async () => {
    setJoinEmail({ email: '', name: '', role: 'player' });
    setShowJoinEmail(true);
    try {
      const res = await axios.get(`${API}/players?team_id=${user.team_id}`);
      const players = (res.data || []).filter((p) => p.email).map((p) => ({ email: p.email, name: p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim(), role: 'player' }));
      const coaches = (teamCoaches || []).filter((c) => c.email && c.id !== user.id).map((c) => ({ email: c.email, name: c.name, role: 'assistant_coach' }));
      setJoinEmailChoices([...players, ...coaches]);
    } catch (e) { setJoinEmailChoices([]); }
  };
  const sendJoinEmail = async () => {
    setIsSendingJoinEmail(true);
    try {
      const res = await axios.post(`${API}/teams/${user.team_id}/join-email`, joinEmail);
      if (res.data.status === 'mocked') toast.warning(res.data.note, { duration: 9000 });
      else toast.success(`Invite email sent to ${res.data.to}`);
      setShowJoinEmail(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite email');
    } finally { setIsSendingJoinEmail(false); }
  };

  const regenerateTeamCode = async () => {
    if (!window.confirm('Issue a new team code? The current code stops working immediately and anyone you already emailed will need the new one.')) return;
    try {
      const res = await axios.post(`${API}/teams/${user.team_id}/regenerate-code`);
      setCurrentTeam((t) => ({ ...t, team_code: res.data.team_code }));
      toast.success(`New team code: ${res.data.team_code}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not regenerate the team code');
    }
  };

  const copyTeamCode = () => {
    if (currentTeam?.team_code) {
      navigator.clipboard.writeText(currentTeam.team_code);
      toast.success('Team code copied to clipboard!');
    }
  };

  const createNewTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    setIsCreatingTeam(true);
    try {
      const response = await axios.post(`${API}/user/create-team?user_id=${user.id}&team_name=${encodeURIComponent(newTeamName)}`);
      toast.success('Team created successfully!');
      setShowCreateTeamDialog(false);
      setNewTeamName('');
      // Reload the page to update team list
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Team limit')) {
        setShowCreateTeamDialog(false);
        setLimitMessage(error.response.data.detail);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create team');
      }
    } finally {
      setIsCreatingTeam(false);
    }
  };

  const scrollToPlans = () => {
    setLimitMessage(null);
    document.querySelector('[data-testid="subscription-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loadArchives = async () => {
    try {
      const response = await axios.get(`${API}/season/archives?team_id=${user.team_id}`);
      setArchives(response.data.archives || []);
    } catch (error) {
      console.error('Failed to load archives');
    }
  };

  const loadInvites = async () => {
    try {
      const response = await axios.get(`${API}/teams/${user.team_id}/invites`);
      setInvites(response.data.invites || []);
    } catch (error) {
      console.error('Failed to load invites');
    }
  };

  const openArchiveViewer = async (archive) => {
    setSelectedArchive(archive);
    setArchiveDetail(null);
    setLoadingArchive(true);
    try {
      const res = await axios.get(`${API}/season/archives/${archive.id}`);
      setArchiveDetail(res.data);
    } catch (error) {
      toast.error('Failed to load archive');
    } finally {
      setLoadingArchive(false);
    }
  };

  const submitInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Enter the coach\'s name and email');
      return;
    }
    const preset = PRESET_ROLES.find(p => p.name === inviteForm.preset) || PRESET_ROLES[0];
    setIsInviting(true);
    try {
      const res = await axios.post(`${API}/teams/${user.team_id}/invite`, {
        email: inviteForm.email.trim(),
        name: inviteForm.name.trim(),
        role: inviteForm.role,
        access: preset.access,
        invited_by: user.id,
      });
      if (res.data.email_status === 'sent') {
        toast.success(`Invite sent to ${inviteForm.email}`);
      } else if (res.data.email_status === 'failed') {
        toast.warning('Invite saved, but email delivery failed. Share the team code manually.');
      } else {
        toast.success('Invite saved. Share the team code with your coach.');
      }
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '', role: 'assistant_coach', preset: 'Full Access' });
      loadInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/teams/${user.team_id}/invites/${inviteId}`);
      toast.success('Invite cancelled');
      loadInvites();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  useEffect(() => {
    if (user.role === 'head_coach') {
      loadArchives();
      loadInvites();
    }
  }, []);

  const performSeasonReset = async () => {
    setIsArchiving(true);
    try {
      const response = await axios.post(`${API}/season/reset`, {
        team_id: user.team_id,
        season_name: seasonName,
        categories: archiveCats
      });
      toast.success(`Season archived! ${response.data.archive.total_items} items saved.`);
      setShowResetConfirm(false);
      setSeasonName('');
      loadArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive season');
    } finally {
      setIsArchiving(false);
    }
  };

  const joinTeam = async () => {
    if (!joinTeamCode.trim()) {
      toast.error('Please enter a team code');
      return;
    }
    setIsJoiningTeam(true);
    try {
      await axios.post(`${API}/teams/join?user_id=${user.id}&team_code=${joinTeamCode.toUpperCase()}`);
      toast.success('Successfully joined the team!');
      setShowJoinTeamDialog(false);
      setJoinTeamCode('');
      // Reload the page to update team list
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join team');
    } finally {
      setIsJoiningTeam(false);
    }
  };

  const getAccessLevel = (coach, area) => {
    const p = coach.permissions;
    if (!p) return 'edit';
    if (p.access && p.access[area]) return p.access[area];
    return p[area] === false ? 'none' : 'edit';
  };

  const applyAccessLocally = (coachId, access) => {
    setTeamCoaches(coaches =>
      coaches.map(c => {
        if (c.id !== coachId) return c;
        const booleans = Object.fromEntries(Object.entries(access).map(([k, v]) => [k, v !== 'none']));
        return {
          ...c,
          permissions: {
            ...(c.permissions || {}),
            ...booleans,
            access: { ...(c.permissions?.access || {}), ...access }
          }
        };
      })
    );
  };

  const updateAreaAccess = async (coachId, area, level) => {
    try {
      await axios.put(`${API}/teams/${user.team_id}/permissions/${coachId}`, {
        access: { [area]: level }
      });
      applyAccessLocally(coachId, { [area]: level });
      toast.success('Permission updated');
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const removeCoachFromTeam = async (coachId, coachName) => {
    if (!window.confirm(`Are you sure you want to remove ${coachName} from this team?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/teams/${user.team_id}/coaches/${coachId}`);
      toast.success('Coach removed from team');
      loadTeamCoaches();
    } catch (error) {
      toast.error('Failed to remove coach');
    }
  };

  const applyPresetRole = async (coachId, preset) => {
    try {
      await axios.put(`${API}/teams/${user.team_id}/permissions/${coachId}`, { access: preset.access });
      applyAccessLocally(coachId, preset.access);
      toast.success(`Applied "${preset.name}" role`);
    } catch (error) {
      toast.error('Failed to apply preset role');
    }
  };

  const saveAlertSchedule = async () => {
    setIsSavingSchedule(true);
    try {
      const response = await axios.post(`${API}/settings/alert-schedule`, {
        team_id: user.team_id,
        ...alertSchedule
      });
      setNextRun(response.data.next_run);
      toast.success('Alert schedule saved!');
    } catch (error) {
      toast.error('Failed to save alert schedule');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const testAlertSchedule = async () => {
    setIsTestingAlert(true);
    try {
      const response = await axios.post(`${API}/settings/alert-schedule/${user.team_id}/test`);
      toast.success(`Test alert sent! ${response.data.emails_sent} email(s) processed.`);
    } catch (error) {
      toast.error('Failed to send test alert');
    } finally {
      setIsTestingAlert(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.id);

    try {
      const response = await axios.post(`${API}/coach-avatar/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarUrl(`${BACKEND_URL}${response.data.avatar_url}`);
      toast.success('AI Coach avatar updated!');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setIsUploading(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      setCameraStream(stream);
      setShowCameraDialog(true);
      
      // Wait for dialog to render, then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      toast.error('Could not access camera. Please check permissions.');
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', blob, 'camera-capture.jpg');
      formData.append('user_id', user.id);

      try {
        const response = await axios.post(`${API}/coach-avatar/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setAvatarUrl(`${BACKEND_URL}${response.data.avatar_url}`);
        toast.success('AI Coach avatar updated!');
        closeCamera();
      } catch (error) {
        toast.error('Failed to save photo');
      } finally {
        setIsUploading(false);
      }
    }, 'image/jpeg', 0.9);
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraDialog(false);
  };

  const removeAvatar = async () => {
    try {
      await axios.delete(`${API}/coach-avatar/${user.id}`);
      setAvatarUrl(null);
      toast.success('Avatar removed');
    } catch (error) {
      toast.error('Failed to remove avatar');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} teams={teams} onSwitchTeam={onSwitchTeam}>
      <div className="p-8 space-y-8 pb-32" data-testid="settings-page">
        <div className="border-b border-border pb-6">
          <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
            Settings
          </h1>
          <p className="text-muted-foreground mt-2">Manage your account and team settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Coach Avatar Card - NEW */}
          <Card className="border-2 border-primary/50 bg-primary/5">
            <CardHeader className="border-b bg-primary/10">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <span className="text-2xl">🤖</span> AI Coach Avatar
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-4">
                Upload or take a photo to personalize your AI coaching assistant. This image will appear in the chatbot on your dashboard.
              </p>
              
              <div className="flex flex-col items-center gap-4">
                {/* Avatar Preview */}
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-muted border-4 border-primary/30 flex items-center justify-center overflow-hidden">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="AI Coach Avatar" 
                        className="w-full h-full object-cover"
                        data-testid="avatar-preview"
                      />
                    ) : (
                      <User className="w-16 h-16 text-muted-foreground" />
                    )}
                  </div>
                  {avatarUrl && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-8 w-8 rounded-full"
                      onClick={removeAvatar}
                      data-testid="remove-avatar-btn"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Upload Options */}
                <div className="flex gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                    data-testid="avatar-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    data-testid="upload-avatar-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={startCamera}
                    disabled={isUploading}
                    data-testid="camera-avatar-btn"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Take Photo
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Tip: Use a clear headshot for the best AI coach experience!
                </p>
              </div>
            </CardContent>
          </Card>

          {/* AI Bot Voice & Tone Card - ElevenLabs */}
          <Card className="border-2 border-violet-500/50 bg-violet-50/40" data-testid="voice-tone-card">
            <CardHeader className="border-b bg-violet-100/60">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Volume2 className="h-6 w-6" /> AI Bot Voice &amp; Tone
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Pick the voice your AI coach uses to read chat replies, game recaps, and scouting reports aloud — and customize its personality.
              </p>

              <div className="space-y-2">
                <Label>Voice</Label>
                <div className="flex gap-2">
                  <Select value={voiceSettings.voice_id} onValueChange={handleVoiceChange}>
                    <SelectTrigger className="flex-1" data-testid="voice-select">
                      <SelectValue placeholder="Select a voice..." />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((v) => (
                        <SelectItem key={v.voice_id} value={v.voice_id} data-testid={`voice-option-${v.name}`}>
                          {v.name} — {v.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={handlePreviewVoice}
                    disabled={isPreviewingVoice}
                    data-testid="preview-voice-btn"
                  >
                    {isPreviewingVoice ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Volume2 className="h-4 w-4" />
                    )}
                    <span className="ml-2">Preview</span>
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tone &amp; Personality</Label>
                <Textarea
                  value={voiceSettings.tone}
                  onChange={(e) => setVoiceSettings((prev) => ({ ...prev, tone: e.target.value }))}
                  placeholder="e.g. Energetic and motivational, like a college coach during March Madness. Keep it upbeat with hype language."
                  rows={3}
                  data-testid="voice-tone-input"
                />
                <p className="text-xs text-muted-foreground">
                  This shapes how your AI coach writes AND speaks its responses.
                </p>
              </div>

              <Button onClick={saveVoiceSettings} disabled={isSavingVoice} data-testid="save-voice-settings-btn">
                {isSavingVoice ? 'Saving...' : 'Save Voice Settings'}
              </Button>
            </CardContent>
          </Card>

          {/* Scheduled Attendance Alerts - NEW */}
          <Card className="border-2 border-orange-500/50 bg-orange-50">
            <CardHeader className="border-b bg-orange-100">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Bell className="h-6 w-6 text-orange-600" />
                Scheduled Attendance Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <p className="text-sm text-muted-foreground">
                Automatically send email notifications to parents when their player's attendance falls below the threshold.
              </p>

              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                <div>
                  <Label className="text-base font-semibold">Enable Automatic Alerts</Label>
                  <p className="text-sm text-muted-foreground">Send scheduled emails to parents</p>
                </div>
                <Switch
                  checked={alertSchedule.enabled}
                  onCheckedChange={(checked) => setAlertSchedule({ ...alertSchedule, enabled: checked })}
                  data-testid="alert-enabled-switch"
                />
              </div>

              {/* Schedule Configuration */}
              <div className={`space-y-4 ${!alertSchedule.enabled && 'opacity-50 pointer-events-none'}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Frequency
                    </Label>
                    <Select
                      value={alertSchedule.frequency}
                      onValueChange={(value) => setAlertSchedule({ ...alertSchedule, frequency: value })}
                    >
                      <SelectTrigger data-testid="alert-frequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {(alertSchedule.frequency === 'weekly' || alertSchedule.frequency === 'biweekly') && (
                    <div>
                      <Label>Day of Week</Label>
                      <Select
                        value={String(alertSchedule.day_of_week)}
                        onValueChange={(value) => setAlertSchedule({ ...alertSchedule, day_of_week: parseInt(value) })}
                      >
                        <SelectTrigger data-testid="alert-day">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day, index) => (
                            <SelectItem key={index} value={String(index)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Time (24h)
                    </Label>
                    <Select
                      value={String(alertSchedule.hour)}
                      onValueChange={(value) => setAlertSchedule({ ...alertSchedule, hour: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="alert-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(24)].map((_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {i.toString().padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Attendance Threshold</Label>
                    <Select
                      value={String(alertSchedule.threshold)}
                      onValueChange={(value) => setAlertSchedule({ ...alertSchedule, threshold: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="alert-threshold">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="90">Below 90%</SelectItem>
                        <SelectItem value="80">Below 80%</SelectItem>
                        <SelectItem value="75">Below 75%</SelectItem>
                        <SelectItem value="70">Below 70%</SelectItem>
                        <SelectItem value="60">Below 60%</SelectItem>
                        <SelectItem value="50">Below 50%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Status */}
              {alertSchedule.enabled && nextRun && (
                <div className="p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge variant="outline" className="bg-green-100 text-green-700">Active</Badge>
                    <span className="text-muted-foreground">
                      Next alert: {new Date(nextRun).toLocaleString()}
                    </span>
                  </div>
                  {lastRun && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Last sent: {new Date(lastRun).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={saveAlertSchedule} 
                  disabled={isSavingSchedule}
                  className="flex-1"
                  data-testid="save-alert-schedule-btn"
                >
                  {isSavingSchedule ? 'SAVING...' : 'SAVE SCHEDULE'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={testAlertSchedule}
                  disabled={isTestingAlert}
                  data-testid="test-alert-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isTestingAlert ? 'SENDING...' : 'TEST NOW'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                <Mail className="h-3 w-3 inline mr-1" />
                Emails are sent to parents with registered email addresses for players below the threshold.
              </p>
            </CardContent>
          </Card>

          {/* Resume Setup — always-available way back into onboarding (owner directive), independent of the dismissible dashboard card */}
          <Card className="border-2">
            <CardContent className="py-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold">Program setup</p>
                <p className="text-sm text-muted-foreground">Pick up where you left off — about you, your program, teams, and goals.</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/onboarding')} data-testid="settings-resume-setup-btn">
                Open setup
              </Button>
            </CardContent>
          </Card>

          {/* Profile Settings */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30 flex flex-row items-center justify-between">
              <CardTitle className="font-heading uppercase text-2xl">Profile Settings</CardTitle>
              {!profileEditable && (
                <span
                  className="flex items-center gap-1 text-xs text-amber-700"
                  title="Your head coach has given you view-only access. Ask them to grant edit rights to update your profile."
                >
                  <Eye className="h-3 w-3" />
                  View-only
                </span>
              )}
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  defaultValue={user.name}
                  disabled={!profileEditable}
                  title={profileEditable ? undefined : 'View-only. Ask your head coach to grant edit access.'}
                  data-testid="profile-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  defaultValue={user.email}
                  type="email"
                  disabled={!profileEditable}
                  title={profileEditable ? undefined : 'View-only. Ask your head coach to grant edit access.'}
                  data-testid="profile-email"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  defaultValue={user.phone || ''}
                  type="tel"
                  disabled={!profileEditable}
                  title={profileEditable ? undefined : 'View-only. Ask your head coach to grant edit access.'}
                  data-testid="profile-phone"
                />
              </div>
              <Button
                className="w-full"
                disabled={!profileEditable}
                data-testid="save-profile-btn"
              >
                SAVE CHANGES
              </Button>
            </CardContent>
          </Card>

          {/* Season Reset (head coach only) */}
          {user.role === 'head_coach' && (
            <Card className="border-2 md:col-span-2" data-testid="season-reset-card">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Archive className="h-6 w-6" />
                  Season Reset
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Archive last season's data and start fresh. Everything you archive is saved safely and listed under Past Seasons below.
                </p>
                <div>
                  <Label>Season Name</Label>
                  <Input
                    placeholder="e.g., 2025-26 Season"
                    value={seasonName}
                    onChange={(e) => setSeasonName(e.target.value)}
                    data-testid="season-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'games', label: 'Games & Stats' },
                    { key: 'schedules', label: 'Schedules' },
                    { key: 'attendance', label: 'Attendance' },
                    { key: 'practice_plans', label: 'Practice Plans' },
                    { key: 'roster', label: 'Roster (Players)' },
                  ].map((cat) => (
                    <label key={cat.key} className="flex items-center gap-2 p-3 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={archiveCats[cat.key]}
                        onChange={(e) => setArchiveCats({ ...archiveCats, [cat.key]: e.target.checked })}
                        className="h-5 w-5 rounded border-2 border-primary"
                        data-testid={`archive-cat-${cat.key}`}
                      />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </label>
                  ))}
                </div>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (!seasonName.trim()) { toast.error('Please enter a season name'); return; }
                    if (!Object.values(archiveCats).some(Boolean)) { toast.error('Select at least one category to archive'); return; }
                    setShowResetConfirm(true);
                  }}
                  data-testid="season-reset-btn"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Archive & Start Fresh
                </Button>
                {archives.length > 0 && (
                  <div className="pt-4 border-t space-y-2" data-testid="past-seasons-list">
                    <p className="font-semibold uppercase text-sm text-muted-foreground">Past Seasons</p>
                    {archives.map((a) => (
                      <div key={a.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                        <div>
                          <p className="font-bold">{a.season_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Archived {new Date(a.archived_at).toLocaleDateString()} — {a.total_items} items
                          </p>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap justify-end">
                          {Object.entries(a.counts || {}).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-xs">{k.replace('_', ' ')}: {v}</Badge>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openArchiveViewer(a)}
                            data-testid={`open-archive-${a.id}`}
                          >
                            <FolderOpen className="h-4 w-4 mr-1" />
                            Open
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Subscription (head coach only) */}
          {user.role === 'head_coach' && (
            <Card className="border-2 md:col-span-2" data-testid="subscription-card">
              <CardHeader className="border-b bg-muted/30">
                <CardTitle className="font-heading uppercase text-2xl flex items-center justify-between">
                  <span>Subscription</span>
                  {subscription && (
                    <Badge className={subscription.subscribed ? 'bg-green-600' : subscription.internal_exempt ? 'bg-slate-800' : subscription.trial ? 'bg-blue-600' : 'bg-gray-500'} data-testid="current-tier-badge">
                      {subscription.subscribed
                        ? `${subscription.tier.toUpperCase()} PLAN`
                        : subscription.internal_exempt
                          ? 'INTERNAL ACCOUNT · ALL FEATURES'
                          : subscription.trial
                            ? `ELITE TRIAL · ${subscription.trial_days_left} DAY${subscription.trial_days_left === 1 ? '' : 'S'} LEFT`
                            : 'NO ACTIVE PLAN'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Choose your plan per season. Payments are processed securely by Stripe.
                  {!subscription?.subscribed && !subscription?.internal_exempt && ' Without a plan, only Basic features are available.'}
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  {plans.map((plan) => {
                    const isCurrent = subscription?.subscribed && subscription.tier === plan.tier;
                    return (
                      <div
                        key={plan.tier}
                        className={`rounded-lg border-2 p-4 flex flex-col ${isCurrent ? 'border-green-600 bg-green-50/50' : plan.tier === 'elite' ? 'border-primary' : 'border-muted'}`}
                        data-testid={`plan-card-${plan.tier}`}
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="font-heading text-xl uppercase font-bold">{plan.name}</h3>
                          {plan.tier === 'elite' && <Badge className="bg-primary">BEST VALUE</Badge>}
                          {isCurrent && <Badge className="bg-green-600">CURRENT</Badge>}
                        </div>
                        <p className="text-3xl font-black mt-2">
                          ${plan.price}
                          <span className="text-sm font-normal text-muted-foreground">/season</span>
                        </p>
                        <p className="text-xs text-muted-foreground mb-3">
                          {plan.team_limit} team{plan.team_limit > 1 ? 's' : ''} per season
                        </p>
                        <ul className="text-sm space-y-1.5 flex-1 mb-4">
                          {plan.features.map((f) => (
                            <li key={f} className="flex items-start gap-2">
                              <span className="text-green-600 mt-0.5">✓</span>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                        <Button
                          className="w-full"
                          variant={isCurrent ? 'outline' : 'default'}
                          disabled={isCurrent || checkoutTier !== null}
                          onClick={() => startCheckout(plan.tier)}
                          data-testid={`choose-plan-${plan.tier}`}
                        >
                          {isCurrent ? 'Current Plan' : checkoutTier === plan.tier ? 'Redirecting...' : subscription?.subscribed ? 'Switch Plan' : 'Choose Plan'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                {subscription?.subscribed && (
                  <div className="pt-3 border-t flex flex-col sm:flex-row sm:items-center gap-2">
                    <p className="text-sm text-muted-foreground flex-1">
                      Manage payment method, invoices or cancel anytime through the secure Stripe portal.
                    </p>
                    <Button
                      variant="outline"
                      onClick={openBillingPortal}
                      disabled={isOpeningPortal}
                      data-testid="manage-subscription-btn"
                    >
                      {isOpeningPortal ? 'Opening...' : 'Manage Subscription'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Team Logo */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl">Team Logo</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload your team's logo to replace PAC branding in the sidebar and all outgoing emails.
              </p>
              <div className="flex items-center gap-4">
                <div className="relative w-24 h-24 bg-muted rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-muted-foreground/30">
                  <img
                    src={currentTeam?.logo_url ? `${BACKEND_URL}${currentTeam.logo_url}` : '/pac-logo.png'}
                    alt="Team logo"
                    className="w-full h-full object-contain"
                    data-testid="team-logo-preview"
                  />
                </div>
                <div className="space-y-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="team-logo-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isUploadingLogo}
                    data-testid="upload-team-logo-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  {currentTeam?.logo_url && (
                    <Button
                      variant="ghost"
                      className="text-destructive block"
                      onClick={handleDeleteLogo}
                      data-testid="remove-team-logo-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove (use PAC logo)
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auto Event Alerts */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Bell className="h-6 w-6" />
                Auto Game &amp; Practice Alerts
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Automatically email the morning of every scheduled game or practice.
              </p>
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch
                  checked={eventAlerts.enabled}
                  onCheckedChange={(v) => setEventAlerts((p) => ({ ...p, enabled: v }))}
                  data-testid="event-alerts-enabled"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Send Email to Parents &amp; Coaches</Label>
                <Switch
                  checked={eventAlerts.send_email}
                  onCheckedChange={(v) => setEventAlerts((p) => ({ ...p, send_email: v }))}
                  data-testid="event-alerts-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Send Time</Label>
                <Select
                  value={String(eventAlerts.send_hour)}
                  onValueChange={(v) => setEventAlerts((p) => ({ ...p, send_hour: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="event-alerts-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, h) => (
                      <SelectItem key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00 UTC {h === 12 ? '(~7-8am US Eastern)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={saveEventAlerts}
                  disabled={isSavingEventAlerts}
                  data-testid="save-event-alerts-btn"
                >
                  {isSavingEventAlerts ? 'Saving...' : 'SAVE ALERT SETTINGS'}
                </Button>
                <Button
                  variant="outline"
                  onClick={testEventAlerts}
                  disabled={isTestingEventAlerts}
                  data-testid="test-event-alerts-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isTestingEventAlerts ? 'Sending...' : 'Test Now'}
                </Button>
              </div>

              {/* Alert History */}
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  className="w-full justify-between text-sm"
                  onClick={toggleAlertHistory}
                  data-testid="toggle-alert-history-btn"
                >
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Alert History
                  </span>
                  <span>{showAlertHistory ? '▲' : '▼'}</span>
                </Button>
                {showAlertHistory && (
                  <div className="mt-2 max-h-64 overflow-y-auto space-y-2" data-testid="alert-history-list">
                    {alertHistory.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-3" data-testid="no-alert-history">
                        No alerts sent yet
                      </p>
                    ) : (
                      alertHistory.map((log) => (
                        <div key={log.id} className="p-3 bg-muted/40 rounded-md border text-sm" data-testid={`alert-log-${log.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">
                              {log.event_type === 'game' ? '🏀' : '📋'} {log.event_title}
                            </span>
                            <div className="flex items-center gap-1">
                              {log.test && <Badge variant="outline" className="text-[10px]">TEST</Badge>}
                              <span className="text-xs text-muted-foreground">{log.date}</span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            📧 {log.emails_sent || 0} email(s)
                            {log.emailed_to?.length > 0 && ` → ${log.emailed_to.slice(0, 3).join(', ')}${log.emailed_to.length > 3 ? ` +${log.emailed_to.length - 3} more` : ''}`}

                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Player Development Digest (head coach only, opt-in) */}
          {user.role === 'head_coach' && (
            <Card className="border-2 border-emerald-500/50 bg-emerald-50/40" data-testid="dev-digest-card">
              <CardHeader className="border-b bg-emerald-100/60">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Bell className="h-6 w-6 text-emerald-700" />
                  Player Development Digest
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Email all coaches a summary of new goals, evaluations, and interventions logged recently.
                </p>
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                  <div>
                    <Label className="text-base font-semibold">Enable Digest</Label>
                    <p className="text-sm text-muted-foreground">Sent automatically to all coaches on this team</p>
                  </div>
                  <Switch
                    checked={devDigestEnabled}
                    onCheckedChange={(checked) => saveDevDigestSettings({ enabled: checked })}
                    disabled={isSavingDevDigest}
                    data-testid="dev-digest-enabled-switch"
                  />
                </div>
                <div className={`grid grid-cols-2 gap-4 ${!devDigestEnabled && 'opacity-50 pointer-events-none'}`}>
                  <div>
                    <Label>Frequency</Label>
                    <Select
                      value={devDigestFrequency}
                      onValueChange={(v) => saveDevDigestSettings({ frequency: v })}
                    >
                      <SelectTrigger data-testid="dev-digest-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {devDigestFrequency !== 'daily' && (
                    <div>
                      <Label>Day of Week</Label>
                      <Select
                        value={String(devDigestDay)}
                        onValueChange={(v) => saveDevDigestSettings({ day_of_week: parseInt(v, 10) })}
                      >
                        <SelectTrigger data-testid="dev-digest-day-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map((day, index) => (
                            <SelectItem key={index} value={String(index)}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={testDevDigest}
                  disabled={isTestingDevDigest}
                  data-testid="test-dev-digest-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isTestingDevDigest ? 'Sending...' : 'Send Test Digest Now'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={toggleDevDigestPreview}
                  disabled={isLoadingDevDigestPreview}
                  data-testid="toggle-dev-digest-preview-btn"
                >
                  {isLoadingDevDigestPreview ? 'Loading...' : devDigestPreview ? 'Hide Preview' : 'Preview Digest'}
                </Button>
                {devDigestPreview && (
                  <div className="p-4 bg-white rounded-lg border space-y-3" data-testid="dev-digest-preview-block">
                    <p className="text-xs text-muted-foreground">
                      Live preview — last {devDigestPreview.lookback_days} day(s) of activity
                    </p>
                    {devDigestPreview.players.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No new development activity in this period.</p>
                    ) : (
                      devDigestPreview.players.map((p) => (
                        <div key={p.player_id} className="p-3 bg-slate-50 border-l-4 border-blue-900 rounded-md">
                          <p className="font-semibold text-sm mb-1">{p.player_name}</p>
                          <ul className="text-sm space-y-0.5 pl-4 list-disc">
                            {p.items.map((item, idx) => <li key={idx}>{item}</li>)}
                          </ul>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Development Categories (head coach only) */}
          {user.role === 'head_coach' && (
            <Card className="border-2 border-indigo-500/50 bg-indigo-50/40" data-testid="dev-categories-card">
              <CardHeader className="border-b bg-indigo-100/60">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Tag className="h-6 w-6 text-indigo-700" />
                  Development Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Customize the categories coaches use when logging goals, evaluations, and interventions.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="dev-categories-list">
                  {devCategories.map((cat) => (
                    <Badge key={cat} variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-sm" data-testid={`dev-category-chip-${cat}`}>
                      {cat}
                      <button
                        type="button"
                        onClick={() => handleRemoveCategory(cat)}
                        disabled={isSavingCategories}
                        data-testid={`remove-category-${cat}`}
                      >
                        <X className="h-3 w-3 hover:text-red-600" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="New category name"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                    data-testid="new-category-input"
                  />
                  <Button onClick={handleAddCategory} disabled={isSavingCategories} data-testid="add-category-btn">
                    <Plus className="h-4 w-4 mr-2" />Add
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team Branding */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl">Team Branding</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {/* Mascot Image Upload */}
              <div className="space-y-2">
                <Label>Mascot Image</Label>
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 bg-muted rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-muted-foreground/30">
                    {teamBranding.mascot_url ? (
                      <>
                        <img 
                          src={teamBranding.mascot_url} 
                          alt="Mascot" 
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={handleDeleteMascot}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          data-testid="delete-mascot-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <Upload className="w-8 h-8 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      ref={mascotInputRef}
                      onChange={handleMascotUpload}
                      className="hidden"
                      data-testid="mascot-file-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => mascotInputRef.current?.click()}
                      disabled={isUploadingMascot}
                      className="w-full"
                      data-testid="upload-mascot-btn"
                    >
                      {isUploadingMascot ? 'Uploading...' : teamBranding.mascot_url ? 'Change Image' : 'Upload Image'}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF up to 5MB</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>School Name</Label>
                <Input 
                  value={teamBranding.school_name}
                  onChange={(e) => setTeamBranding(prev => ({ ...prev, school_name: e.target.value }))}
                  placeholder="Your School Name" 
                  data-testid="school-name" 
                />
              </div>
              <div>
                <Label>Mascot Name</Label>
                <Input 
                  value={teamBranding.mascot_name}
                  onChange={(e) => setTeamBranding(prev => ({ ...prev, mascot_name: e.target.value }))}
                  placeholder="Leopards"
                  data-testid="mascot-name" 
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Primary Color</Label>
                  <Input 
                    type="color" 
                    value={teamBranding.primary_color}
                    onChange={(e) => setTeamBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                    data-testid="primary-color" 
                  />
                </div>
                <div>
                  <Label>Secondary Color</Label>
                  <Input 
                    type="color" 
                    value={teamBranding.secondary_color}
                    onChange={(e) => setTeamBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                    data-testid="secondary-color" 
                  />
                </div>
                <div>
                  <Label>Accent Color</Label>
                  <Input 
                    type="color" 
                    value={teamBranding.accent_color}
                    onChange={(e) => setTeamBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                    data-testid="accent-color" 
                  />
                </div>
              </div>
              <Button 
                className="w-full" 
                data-testid="save-branding-btn"
                onClick={handleSaveBranding}
                disabled={isSavingBranding}
              >
                {isSavingBranding ? 'SAVING...' : 'SAVE BRANDING'}
              </Button>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl">Notification Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <Label>Email Notifications</Label>
                <input type="checkbox" defaultChecked data-testid="email-notifications" />
              </div>
              <div className="flex items-center justify-between">
                <Label>SMS Notifications</Label>
                <input type="checkbox" defaultChecked data-testid="sms-notifications" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Practice Reminders</Label>
                <input type="checkbox" defaultChecked data-testid="practice-reminders" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Game Reminders</Label>
                <input type="checkbox" defaultChecked data-testid="game-reminders" />
              </div>
              <Button className="w-full" data-testid="save-notifications-btn">SAVE PREFERENCES</Button>
            </CardContent>
          </Card>

          {/* Transportation File (upload + view - team-wide latest overwrites) */}
          <Card className="border-2 lg:col-span-2" data-testid="transportation-settings-box">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Bus className="h-6 w-6 text-primary" />
                Transportation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload your team&apos;s transportation Excel — the latest file replaces the previous one. The Dashboard&apos;s
                &quot;Download Transportation Template&quot; button and the View button below both serve this file.
              </p>
              {transportationFile ? (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md" data-testid="transportation-current">
                  <div>
                    <p className="font-semibold">{transportationFile.original_filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {(transportationFile.size_bytes / 1024).toFixed(1)} KB · Uploaded {transportationFile.uploaded_at ? new Date(transportationFile.uploaded_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={viewTransportationFile} data-testid="view-transportation-btn">
                    <Download className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </div>
              ) : (
                <p className="text-sm italic text-muted-foreground">No file uploaded yet — the built-in blank template will be served.</p>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={transportationFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm"
                  onChange={handleTransportationUpload}
                  className="hidden"
                  data-testid="transportation-file-input"
                />
                <Button
                  onClick={() => transportationFileInputRef.current?.click()}
                  disabled={isUploadingTransportation}
                  data-testid="upload-transportation-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isUploadingTransportation ? 'Uploading…' : (transportationFile ? 'Replace File' : 'Upload File')}
                </Button>
              </div>

              {/* Auto-Reminder pipeline preview */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold">Auto Bus-Company Reminders</p>
                    <p className="text-xs text-muted-foreground">
                      Runs hourly. ~48 hours before each away game, we&apos;ll SMS the driver phone from the row that matches the game&apos;s date. Sends only once per game.
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={loadReminderPreview} data-testid="transport-reminder-preview-btn">
                      Preview
                    </Button>
                    <Button size="sm" onClick={runReminderNow} disabled={isRunningReminders} data-testid="transport-reminder-run-btn">
                      {isRunningReminders ? 'Sending…' : 'Send Pending Now'}
                    </Button>
                  </div>
                </div>
                {reminderPreview && (
                  <div className="border rounded p-2 text-xs" data-testid="transport-reminder-preview">
                    {reminderPreview.items?.length === 0 ? (
                      <p className="italic text-muted-foreground">No upcoming away games in the next 7 days.</p>
                    ) : (
                      <div className="divide-y">
                        {reminderPreview.items.map((it) => (
                          <div key={it.game_id} className="py-1 flex items-center justify-between gap-2" data-testid={`transport-reminder-row-${it.game_id}`}>
                            <span className="flex-1 min-w-0 truncate">
                              <b>{it.opponent || 'Away game'}</b> · {String(it.date || '').split('T')[0]}
                            </span>
                            <span className={`ml-2 shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${
                              it.already_sent ? 'bg-green-100 text-green-700'
                              : it.will_send ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                              {it.already_sent ? 'ALREADY SENT' : it.will_send ? `WILL TEXT ${it.phone || ''}` : 'NO PHONE ON ROW'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Team Meal File (upload + view - auto-generates a per-game workbook when no upload) */}
          {user.role === 'head_coach' && (
            <Card className="border-2 border-orange-500/50 bg-orange-50 lg:col-span-2" data-testid="team-meal-file-box">
              <CardHeader className="border-b bg-orange-100">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Utensils className="h-6 w-6 text-orange-600" />
                  Team Meal Workbook
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Upload the coach-maintained team meal Excel (latest overwrites). Clicking <b>View</b> when no upload
                  exists auto-generates a template with <b>one worksheet per game</b> — every player as a row with Meal Choice, Allergies, Notes.
                </p>
                {teamMealFile ? (
                  <div className="flex items-center justify-between p-3 bg-white rounded-md border" data-testid="team-meal-current">
                    <div>
                      <p className="font-semibold">{teamMealFile.original_filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {(teamMealFile.size_bytes / 1024).toFixed(1)} KB · Uploaded {teamMealFile.uploaded_at ? new Date(teamMealFile.uploaded_at).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    No upload yet — clicking View will generate a fresh workbook from your current schedule and roster.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <input
                    ref={teamMealFileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.xlsm"
                    onChange={handleTeamMealUpload}
                    className="hidden"
                    data-testid="team-meal-file-input"
                  />
                  <Button
                    onClick={() => teamMealFileInputRef.current?.click()}
                    disabled={isUploadingTeamMeal}
                    data-testid="upload-team-meal-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploadingTeamMeal ? 'Uploading…' : (teamMealFile ? 'Replace File' : 'Upload File')}
                  </Button>
                  <Button variant="outline" onClick={viewTeamMealFile} data-testid="view-team-meal-btn">
                    <Download className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Links Configuration - Only for Head Coach and Admin */}
          {user.role === 'head_coach' && (
            <Card className="border-2 border-orange-500/50 bg-orange-50 lg:col-span-2">
              <CardHeader className="border-b bg-orange-100">
                <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                  <Utensils className="h-6 w-6 text-orange-600" />
                  Team Meal Providers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <p className="text-sm text-muted-foreground">
                  Add meal ordering services for your team. These links will appear in the Quick Actions menu on the dashboard.
                </p>
                
                {/* Existing Providers */}
                {quickLinks.meal_providers.length > 0 && (
                  <div className="space-y-3">
                    {quickLinks.meal_providers.map((provider, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <Utensils className="h-5 w-5 text-orange-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{provider.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{provider.url}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeMealProvider(index)}
                          data-testid={`remove-meal-provider-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Provider */}
                <div className="p-4 bg-white rounded-lg border space-y-4">
                  <Label className="font-semibold">Add New Meal Provider</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Provider Name</Label>
                      <Input
                        placeholder="e.g., DoorDash, Uber Eats, Local Pizza"
                        value={newMealProvider.name}
                        onChange={(e) => setNewMealProvider({...newMealProvider, name: e.target.value})}
                        data-testid="new-meal-provider-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Website URL</Label>
                      <Input
                        placeholder="https://doordash.com/business"
                        value={newMealProvider.url}
                        onChange={(e) => setNewMealProvider({...newMealProvider, url: e.target.value})}
                        data-testid="new-meal-provider-url"
                      />
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={addMealProvider}
                    className="w-full"
                    data-testid="add-meal-provider-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Provider
                  </Button>
                </div>

                <Button 
                  onClick={handleSaveQuickLinks} 
                  disabled={isSavingQuickLinks}
                  className="w-full"
                  data-testid="save-quick-links-btn"
                >
                  {isSavingQuickLinks ? 'SAVING...' : 'SAVE MEAL PROVIDERS'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Team Management */}
          <Card className="border-2 border-blue-500/50 bg-blue-50 lg:col-span-2">
            <CardHeader className="border-b bg-blue-100">
              <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                Team Management
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Current Team Info */}
              {currentTeam && (
                <div className="p-4 bg-white rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-muted-foreground">Current Team</Label>
                      <p className="text-xl font-bold">{currentTeam.name}</p>
                    </div>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700">
                      {user.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
                    </Badge>
                  </div>
                  
                  {/* Team Code - Only visible to head coach */}
                  {user.role === 'head_coach' && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Team Code (Share with assistant coaches to join)
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-2xl font-mono font-bold tracking-wider text-blue-600" data-testid="team-code">
                          {currentTeam.team_code}
                        </code>
                        <Button variant="outline" size="sm" onClick={copyTeamCode} data-testid="copy-team-code-btn">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={openJoinEmail} data-testid="email-team-code-btn">
                          <Mail className="h-4 w-4 mr-1" /> Email
                        </Button>
                        <Button variant="ghost" size="sm" onClick={regenerateTeamCode} data-testid="regenerate-team-code-btn" title="Issue a new code; the old one stops working">
                          <RefreshCw className="h-4 w-4 mr-1" /> New code
                        </Button>
                      </div>
                      <Dialog open={showJoinEmail} onOpenChange={setShowJoinEmail}>
                        <DialogContent data-testid="join-email-dialog">
                          <DialogHeader><DialogTitle className="font-heading uppercase">Email an invite to join {currentTeam.name}</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            {joinEmailChoices.length > 0 && (
                              <div>
                                <Label className="text-xs">Pick a player or coach</Label>
                                <Select onValueChange={(v) => { const c = joinEmailChoices.find((x) => x.email === v); if (c) setJoinEmail({ ...joinEmail, email: c.email, name: c.name, role: c.role }); }}>
                                  <SelectTrigger data-testid="join-email-pick"><SelectValue placeholder="Choose from the roster…" /></SelectTrigger>
                                  <SelectContent>
                                    {joinEmailChoices.map((c) => <SelectItem key={c.email} value={c.email}>{c.name} — {c.email} ({c.role === 'player' ? 'player' : 'coach'})</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div><Label className="text-xs">Email address</Label><Input type="email" value={joinEmail.email} onChange={(e) => setJoinEmail({ ...joinEmail, email: e.target.value })} placeholder="name@example.com" data-testid="join-email-address" /></div>
                            <div><Label className="text-xs">Name (optional)</Label><Input value={joinEmail.name} onChange={(e) => setJoinEmail({ ...joinEmail, name: e.target.value })} data-testid="join-email-name" /></div>
                            <div>
                              <Label className="text-xs">They will join as</Label>
                              <Select value={joinEmail.role} onValueChange={(v) => setJoinEmail({ ...joinEmail, role: v })}>
                                <SelectTrigger data-testid="join-email-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="player">Player</SelectItem>
                                  <SelectItem value="assistant_coach">Assistant coach</SelectItem>
                                  <SelectItem value="parent">Parent / guardian</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <p className="text-xs text-muted-foreground">The email includes the team code <code className="font-mono font-bold">{currentTeam.team_code}</code> and the steps to register.</p>
                            <Button onClick={sendJoinEmail} disabled={isSendingJoinEmail || !joinEmail.email} className="w-full" data-testid="join-email-send-btn">
                              {isSendingJoinEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} SEND INVITE EMAIL
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              )}

              {/* Team Actions */}
              <div className="flex gap-3">
                {user.role === 'head_coach' && (
                  <Button onClick={() => setShowCreateTeamDialog(true)} data-testid="create-team-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Team
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowJoinTeamDialog(true)} data-testid="join-team-btn">
                  <Users className="h-4 w-4 mr-2" />
                  Join Team with Code
                </Button>
              </div>

              {/* Team List */}
              {teams.length > 1 && (
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Your Teams ({teams.length})</Label>
                  <div className="space-y-2">
                    {teams.map(team => (
                      <div 
                        key={team.id}
                        className={`p-3 rounded-lg border flex items-center justify-between ${team.id === user.team_id ? 'bg-blue-100 border-blue-300' : 'bg-white'}`}
                      >
                        <div>
                          <p className="font-semibold">{team.name}</p>
                          {team.id === user.team_id && (
                            <Badge variant="secondary" className="text-xs">Active</Badge>
                          )}
                        </div>
                        {team.id !== user.team_id && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onSwitchTeam && onSwitchTeam(team.id)}
                          >
                            Switch
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Coach Permissions - Only for Head Coach */}
          {user.role === 'head_coach' && (
            <Card className="border-2 border-purple-500/50 bg-purple-50 lg:col-span-2">
              <CardHeader className="border-b bg-purple-100">
                <CardTitle className="font-heading uppercase text-2xl flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <Shield className="h-6 w-6 text-purple-600" />
                    Coach Permissions
                  </span>
                  <Button size="sm" onClick={() => setShowInviteDialog(true)} data-testid="invite-coach-btn">
                    <Mail className="h-4 w-4 mr-2" />
                    Invite Coach by Email
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  All coach accounts and coaching staff profiles are listed here. Give each coach View or Edit access for every area of the app — Edit includes View, unchecking View removes access.
                </p>

                {invites.filter(i => i.status === 'pending').length > 0 && (
                  <div className="border rounded-md p-3 bg-white space-y-2" data-testid="pending-invites">
                    <p className="text-xs font-bold uppercase text-muted-foreground">Pending Invites</p>
                    {invites.filter(i => i.status === 'pending').map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="font-semibold">{inv.invited_name}</span>
                          <span className="text-muted-foreground"> · {inv.invited_email}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {inv.role === 'head_coach' ? 'Head Coach' : 'Assistant'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/teams/${user.team_id}/invites/${inv.id}/resend`);
                                if (res.data.email_status === 'sent') toast.success(`Reminder emailed to ${inv.invited_email}`);
                                else if (res.data.email_status === 'failed') toast.warning('Email failed — share the signup link manually');
                                else toast.info('Invite refreshed');
                                loadInvites();
                              } catch (err) {
                                toast.error('Failed to resend invite');
                              }
                            }}
                            data-testid={`resend-invite-${inv.id}`}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Resend
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = `${window.location.origin}/?team_code=${currentTeam?.team_code || ''}`;
                              navigator.clipboard.writeText(link);
                              toast.success('Signup link copied');
                            }}
                            data-testid={`copy-invite-link-${inv.id}`}
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => cancelInvite(inv.id)}
                            data-testid={`cancel-invite-${inv.id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {teamCoaches.length > 0 ? (
                  <div className="space-y-4">
                    {teamCoaches.filter(c => c.id !== user.id).map(coach => (
                      <div key={coach.id} className="p-4 bg-white rounded-lg border" data-testid={`coach-perm-card-${coach.id}`}>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="font-bold">{coach.name}</p>
                            <p className="text-sm text-muted-foreground">{coach.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={coach.role === 'head_coach' ? 'default' : 'outline'}>
                              {coach.role === 'head_coach' ? 'Head Coach' : coach.is_profile ? 'Staff Profile' : 'Assistant'}
                            </Badge>
                            {coach.role !== 'head_coach' && !coach.is_profile && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => removeCoachFromTeam(coach.id, coach.name)}
                                data-testid={`remove-coach-${coach.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Permission grid for assistant coaches & staff profiles */}
                        {coach.role !== 'head_coach' && (
                          <div className="space-y-3">
                            {/* Preset Role Selector */}
                            <div className="flex items-center gap-2 mb-3">
                              <Label className="text-sm font-medium">Quick Assign Role:</Label>
                              <Select onValueChange={(value) => {
                                const preset = PRESET_ROLES.find(p => p.name === value);
                                if (preset) applyPresetRole(coach.id, preset);
                              }}>
                                <SelectTrigger className="w-48" data-testid={`preset-role-${coach.id}`}>
                                  <SelectValue placeholder="Select preset..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {PRESET_ROLES.map((preset) => (
                                    <SelectItem key={preset.name} value={preset.name}>
                                      <div>
                                        <span className="font-medium">{preset.name}</span>
                                        <span className="text-xs text-muted-foreground ml-2">({preset.description})</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {/* View/Edit access grid per area */}
                            <div className="border rounded-md overflow-hidden">
                              <div className="grid grid-cols-[1fr_70px_70px] bg-gray-100 px-3 py-2">
                                <span className="text-xs font-bold uppercase text-muted-foreground">Area</span>
                                <span className="text-xs font-bold uppercase text-muted-foreground text-center">View</span>
                                <span className="text-xs font-bold uppercase text-muted-foreground text-center">Edit</span>
                              </div>
                              {Object.entries(PERMISSION_LABELS).map(([key, label]) => {
                                const level = getAccessLevel(coach, key);
                                return (
                                  <div key={key} className="grid grid-cols-[1fr_70px_70px] items-center px-3 py-2 border-t hover:bg-gray-50">
                                    <Label className="text-sm">{label}</Label>
                                    <div className="text-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-2 border-primary cursor-pointer"
                                        checked={level !== 'none'}
                                        onChange={(e) => updateAreaAccess(coach.id, key, e.target.checked ? 'view' : 'none')}
                                        data-testid={`perm-view-${coach.id}-${key}`}
                                      />
                                    </div>
                                    <div className="text-center">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-2 border-primary cursor-pointer"
                                        checked={level === 'edit'}
                                        onChange={(e) => updateAreaAccess(coach.id, key, e.target.checked ? 'edit' : 'view')}
                                        data-testid={`perm-edit-${coach.id}-${key}`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <p className="text-muted-foreground">No assistant coaches on this team yet.</p>
                    <p className="text-sm text-muted-foreground mt-1">Share your team code to invite coaches!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Team Dialog */}
      <Dialog open={showCreateTeamDialog} onOpenChange={setShowCreateTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">Create New Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team Name</Label>
              <Input 
                placeholder="e.g., Varsity Basketball"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                data-testid="new-team-name-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateTeamDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createNewTeam} disabled={isCreatingTeam} data-testid="confirm-create-team-btn">
                {isCreatingTeam ? 'Creating...' : 'Create Team'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Season Reset Confirm Dialog */}
      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent data-testid="season-reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">Confirm Season Reset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will archive <strong>{Object.entries(archiveCats).filter(([, v]) => v).map(([k]) => k.replace('_', ' ')).join(', ')}</strong> under
              "<strong>{seasonName}</strong>" and clear them from your current view. Archived data stays saved. Continue?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowResetConfirm(false)}>Cancel</Button>
              <Button variant="destructive" onClick={performSeasonReset} disabled={isArchiving} data-testid="confirm-season-reset-btn">
                {isArchiving ? 'Archiving...' : 'Yes, Archive Season'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Limit Reached Dialog */}
      <Dialog open={!!limitMessage} onOpenChange={(open) => !open && setLimitMessage(null)}>
        <DialogContent data-testid="team-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">Team Limit Reached</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground" data-testid="team-limit-message">{limitMessage}</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setLimitMessage(null)} data-testid="team-limit-dismiss-btn">
                Not Now
              </Button>
              <Button onClick={scrollToPlans} data-testid="team-limit-upgrade-btn">
                View Plans
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join Team Dialog */}
      <Dialog open={showJoinTeamDialog} onOpenChange={setShowJoinTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">Join Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter the team code provided by your head coach to join their team.
            </p>
            <div>
              <Label>Team Code</Label>
              <Input 
                placeholder="Enter 8-character code"
                value={joinTeamCode}
                onChange={(e) => setJoinTeamCode(e.target.value.toUpperCase())}
                maxLength={8}
                className="font-mono text-lg uppercase tracking-wider"
                data-testid="join-team-code-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowJoinTeamDialog(false)}>
                Cancel
              </Button>
              <Button onClick={joinTeam} disabled={isJoiningTeam} data-testid="confirm-join-team-btn">
                {isJoiningTeam ? 'Joining...' : 'Join Team'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCameraDialog} onOpenChange={(open) => !open && closeCamera()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">Take Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                muted 
                className="w-full h-full object-cover"
              />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={closeCamera}>
                Cancel
              </Button>
              <Button onClick={capturePhoto} disabled={isUploading} data-testid="capture-photo-btn">
                <Camera className="h-4 w-4 mr-2" />
                {isUploading ? 'Saving...' : 'Capture Photo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Coach Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-lg" data-testid="invite-coach-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Invite Coach by Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We'll email your team code along with the exact per-area access you pick. When they sign up, their permissions will be pre-applied.
            </p>
            <div>
              <Label>Coach Name</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Jamie Rivera"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="jamie@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                  <SelectItem value="head_coach">Head Coach</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteForm.role === 'assistant_coach' && (
              <div>
                <Label>Access Preset</Label>
                <Select
                  value={inviteForm.preset}
                  onValueChange={(v) => setInviteForm({ ...inviteForm, preset: v })}
                >
                  <SelectTrigger data-testid="invite-preset-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESET_ROLES.map((preset) => (
                      <SelectItem key={preset.name} value={preset.name}>
                        {preset.name} — {preset.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
              <Button onClick={submitInvite} disabled={isInviting} data-testid="submit-invite-btn">
                {isInviting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {isInviting ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Viewer Dialog */}
      <Dialog open={!!selectedArchive} onOpenChange={(open) => { if (!open) { setSelectedArchive(null); setArchiveDetail(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="archive-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              {selectedArchive?.season_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingArchive && (
              <div className="text-center py-8 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading archive...
              </div>
            )}
            {!loadingArchive && archiveDetail && (
              <>
                <p className="text-sm text-muted-foreground">
                  Archived {new Date(archiveDetail.archive.archived_at).toLocaleString()} · {archiveDetail.archive.total_items} items
                </p>
                {Object.keys(archiveDetail.docs_by_category || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No documents in this archive.</p>
                ) : (
                  Object.entries(archiveDetail.docs_by_category).map(([category, collections]) => (
                    <div key={category} className="border rounded-md" data-testid={`archive-cat-block-${category}`}>
                      <div className="bg-muted px-3 py-2 border-b flex items-center justify-between">
                        <span className="font-bold uppercase text-sm">{category.replace('_', ' ')}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!window.confirm(`Restore all ${category.replace('_', ' ')} items from "${selectedArchive.season_name}" into the live team? Existing items will be skipped.`)) return;
                            try {
                              const res = await axios.post(`${API}/season/archives/${selectedArchive.id}/restore`, {
                                category,
                                remove_from_archive: false,
                              });
                              toast.success(res.data.message);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to restore');
                            }
                          }}
                          data-testid={`restore-${category}-btn`}
                        >
                          Restore
                        </Button>
                      </div>
                      <div className="p-3 space-y-3">
                        {Object.entries(collections).map(([collection, docs]) => (
                          <div key={collection}>
                            <p className="text-xs uppercase text-muted-foreground mb-1">{collection} ({docs.length})</p>
                            <div className="space-y-1 max-h-64 overflow-y-auto">
                              {docs.slice(0, 100).map((doc, i) => (
                                <div key={i} className="text-sm bg-muted/40 rounded px-2 py-1">
                                  {doc.first_name || doc.last_name ? (
                                    <span>
                                      <span className="font-semibold">{doc.first_name} {doc.last_name}</span>
                                      {doc.jersey_number != null && <span className="text-muted-foreground"> · #{doc.jersey_number}</span>}
                                      {doc.position && <span className="text-muted-foreground"> · {doc.position}</span>}
                                    </span>
                                  ) : doc.title || doc.name || doc.game_name ? (
                                    <span>
                                      <span className="font-semibold">{doc.title || doc.name || doc.game_name}</span>
                                      {doc.date && <span className="text-muted-foreground"> · {new Date(doc.date).toLocaleDateString()}</span>}
                                      {doc.opponent && <span className="text-muted-foreground"> · vs {doc.opponent}</span>}
                                      {doc.location && <span className="text-muted-foreground"> · {doc.location}</span>}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">{JSON.stringify(doc).slice(0, 140)}...</span>
                                  )}
                                </div>
                              ))}
                              {docs.length > 100 && (
                                <p className="text-xs text-muted-foreground italic">... and {docs.length - 100} more</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
